import React, {useEffect, useState} from 'react';

import { useTranslation } from 'react-i18next';

import { LinearProgress, styled, Card, CardHeader, CardContent, Tooltip } from "@mui/material";

import useDataSource from "../../../../hooks/useDataSource";
import * as DateUtilities from "../../../../utilities/DateUtilities";
import {getInsightDateVariance, getInsightNumberVariance} from "../../../../utilities/InsightUtilities";
import ClearFilterBtn from "../../../application/ClearFilterBtn";

import {
    getFieldFormatters
} from "../../../../utilities/DataSourceUtilities";

import {
    formatDate, formatDaysRemaining, formatDuration, formatNumber,
    formatStyle, formatPrefix, formatSuffix, formatText
} from "../../../../utilities/FormatUtilities";
import * as DataUtilities from "../../../../utilities/DataUtilities";
import {useDispatch, useSelector} from "react-redux";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

const PREFIX = 'SmartDate';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`,
    fancyDate: `${PREFIX}-fancyDate`,
    day: `${PREFIX}-day`,
    month: `${PREFIX}-month`,
    year: `${PREFIX}-year`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none'
    },

    [`& .${classes.fancyDate}`]: {
        float: 'left',
        // top: '50%',
        left: '45%',
        transform: 'translate(-50%, 0%)',
        position: 'relative',
        padding: '55px 5px 0px 30px'
    },

    [`& .${classes.day}`]: {
        top: '10px',
        // left: '15px',
        right: '35px',
        fontSize: '5em',
        lineHeight: '45px',
        position: 'absolute'
    },

    [`& .${classes.month}`]: {
        textTransform: 'uppercase',
        fontSize: '2em',
        margin: '0px 0px 0px 0px'
    },

    [`& .${classes.year}`]: {
        top: '15px',
        right: '-10px',
        display: 'block',
        position: 'absolute',
        transform: 'rotate(-90deg)',
        fontSize: '1.5em',
        fontWeight: 'bold'
    }
}));

export default function SmartDate({id, configuration, xId}) {

	const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    // App State
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [day, setDay] = useState('--');
    const [month, setMonth] = useState('--');
    const [year, setYear] = useState('--');
    const [insight, setInsight] = useState(null);
    const [showProgress, setShowProgress] = useState(compConfig.element?.showFetching !== undefined ? compConfig.element?.showFetching : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [insightContentStyle, setInsightContentStyle] = useState({});

	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const field = compConfig.dataSource?.field ? compConfig.dataSource?.field : {};
	const styles = compConfig?.element?.styles;
	const condition = compConfig.dataSource?.condition ? compConfig.dataSource?.condition : null;
	const insightConfig = compConfig.dataSource?.insight ? compConfig.dataSource?.insight : {enabled: false};

	const [insightTooltip, setInsightTooltip] = useState(insightConfig?.effect?.metric?.tooltip !== undefined ? insightConfig.effect.metric.tooltip : '');

	const dataSource = useDataSource(dataSourceName);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

    // Data
    useEffect(() => {
        // console.log('SmartDate: useEffect([dataSource.data])', dataSource.data);

        if (dataSource.data && dataSource.fetched) {

            let data =[];
            if (condition) {
                let conditionValue;
                switch (condition.conditionValue.toLowerCase()) {
                    default:
                        conditionValue = condition.conditionValue
                        break;
                }
                switch (condition.conditionType.toLowerCase()) {
                    default:
                        conditionValue = condition.conditionValue
                        break;
                }
                const newData = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, conditionValue, dataSource.data, null);
                // If condition found, push, otherwise, keep null/empty
                if (newData !== undefined) {
                    data.push(newData);
                }
            } else {
                data = dataSource.data;
            }

            if (data.length === 1) {
                const theDate = {
                    day: (data[0][field.name] ? DateUtilities.getDateNode(data[0][field.name], 'day') : '--'),
                    month: (data[0][field.name] ? DateUtilities.getDateNode(data[0][field.name], 'month') : '--'),
                    year: (data[0][field.name] ? DateUtilities.getDateNode(data[0][field.name], 'year') : '--')
                };

                let insightValue = {};
                let insightFormatters = [];
                let formattedInsight;
                if (insightConfig.enabled) {

                    // Filter DataSet for Insight
                    let conditionValue, baselineData;
                    if (insightConfig?.condition !== undefined) {
                        switch (insightConfig.condition.conditionValue.toLowerCase()) {
                            default:
                                conditionValue = insightConfig.condition.conditionValue
                                break;
                        }
                        switch (insightConfig.condition.conditionType.toLowerCase()) {
                            default:
                                conditionValue = insightConfig.condition.conditionValue
                                break;
                        }
                        baselineData = DataUtilities.filterDataSet(insightConfig.condition.conditionType, insightConfig.condition.conditionOperator, insightConfig.condition.conditionField, conditionValue, dataSource.data, null);
                    } else {
                        baselineData = data[0];
                    }

                    // Insight Type
                    if (insightConfig.type.toLowerCase() === 'datevariance') {
                        insightValue = getInsightDateVariance(data[0][field.name], baselineData, insightConfig);
                    } else if (insightConfig.type.toLowerCase() === 'numbervariance') {
                        insightValue = getInsightNumberVariance(data[0][field.name], baselineData, insightConfig);
                    }

                    // Format the Insights Metric Value
                    if (insightValue?.formatActualAndInsight !== undefined) {
                        if (insightValue.formatActualAndInsight) {
                            insightFormatters = insightConfig.effect.metric.formatters;
                            formattedInsight = insightValue.metric;
                            for (const formatter of insightFormatters) {
                                if (formatter.type.toLowerCase() === 'prefix') {
                                    formattedInsight = formatPrefix(formattedInsight, formatter.config);
                                } else if (formatter.type.toLowerCase() === 'style') {
                                    formattedInsight = formatStyle(formattedInsight, formatter.config);
                                } else if (formatter.type.toLowerCase() === 'text') {
                                    formattedInsight = formatText(formattedInsight, formatter.config);
                                } else if (formatter.type.toLowerCase() === 'suffix') {
                                    formattedInsight = formatSuffix(formattedInsight, formatter.config);
                                }
                            }
                        }
                    }
                }

                // Format Actual Value
                let formatters = [];
                if (insightValue?.formatActualAndInsight !== undefined) {
                    if (insightValue.formatActualAndInsight) {
                        formatters = insightConfig.effect.actual.formatters;
                    } else {
                        formatters = field?.formatters.length > 0 ? field.formatters : getFieldFormatters(dataSource, field.name);
                    }
                } else {
                    formatters = field?.formatters.length > 0 ? field.formatters : getFieldFormatters(dataSource, field.name);
                }

                let formattedActualValue = theDate;
                for (const formatter of formatters) {
                    if (formatter.type.toLowerCase() === 'style') {
                        formattedActualValue.day = formatStyle(formattedActualValue.day, formatter.config);
                        formattedActualValue.month = formatStyle(formattedActualValue.month, formatter.config);
                        formattedActualValue.year = formatStyle(formattedActualValue.year, formatter.config);
                    }
                }

                setDay(formattedActualValue ? formattedActualValue.day : '--');
                setMonth(formattedActualValue ? formattedActualValue.month : '--');
                setYear(formattedActualValue ? formattedActualValue.year : '--');
                setInsight(formattedInsight ? formattedInsight : null);
            } else {
                setDay('--');
                setMonth('--');
                setYear('--');
            }
        } else {
            setDay('--');
            setMonth('--');
            setYear('--');
        }
    }, [dataSource.data]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'SmartDate Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'SmartDate'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {
			...styles?.cardContent
			, height: 'calc(100% - 49px)'
			, overflow: 'hidden'
			, padding: '0px'
			, display: 'flex'
			, flexDirection: 'column'
			, justifyContent: 'center'
		});
		const newInsightContentStyle = Object.assign({}, {...insightConfig?.effect?.metric?.styles?.insightContent});

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
		setInsightContentStyle(newInsightContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <div> {/* This DIV is needed to center date within the Card */}
						<div className={classes.fancyDate}>
							<span className={classes.day}>{day}</span>
							<span className={classes.month}>{month}</span>
							<span className={classes.year}>{year}</span>
						</div>
						{insight &&
							<Tooltip title={insightTooltip} arrow placement="top">
								<div style={insightContentStyle}>
									{insight}
								</div>
							</Tooltip>
						}
					</div>
                </CardContent>
            </Card>

        </Root>
    );
}
